import React, { useState, useMemo } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  ATHLETES, ME, Org, OrgRole, GLOBAL_ORG_ID, AthleteKind, KIND_LABEL,
  isOrgManager,
} from "../mockData";
import { Sparkline } from "../components/StatCard";
import { RiskBadge } from "../components/RiskBadge";
import { FilterCard, OptionFilter } from "../components/VisualFilter";
import { Search, Plus, UserPlus, MessageCircle, Globe2, Building2 } from "lucide-react";

export default function Roster() {
  const { org, role } = useOutletContext<{ org: Org; role: OrgRole }>();
  const isManager = isOrgManager(role);
  const [q, setQ] = useState("");
  const [sport, setSport] = useState("all");

  // Scope by org. Eleetz Global shows the public pool (everyone except self
  // for members). Paid orgs only show their own people. Roster mixes kinds
  // freely — a sports academy can have employees, a workplace can have a
  // visiting coach (client), etc.
  const orgPool = org.id === GLOBAL_ORG_ID
    ? ATHLETES
    : ATHLETES.filter((a) => a.orgId === org.id);
  const pool = isManager ? orgPool : orgPool.filter((a) => a.id !== ME.athleteId);

  const sports = useMemo(() => Array.from(new Set(pool.map((a) => a.activity))), [pool]);

  const filtered = pool.filter((a) => {
    if (sport !== "all" && a.activity !== sport) return false;
    if (q && !a.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  // pool already filters self in personal view; filter logic uses isManager from context
  const sportEmoji: Record<string, string> = {
    Golf: "🏌️", Tennis: "🎾", Cricket: "🏏", Bowling: "🎳",
  };

  return (
    <div className="space-y-4">
      <FilterCard
        countActive={(sport !== "all" ? 1 : 0) + (q ? 1 : 0)}
        rightHint={
          <div className="hidden sm:flex items-center gap-2">
            <button className="btn-secondary !h-9">
              <UserPlus className="h-4 w-4" />
              {isManager ? "Invite" : "Find friends"}
            </button>
            <button className="btn-primary !h-9">
              <Plus className="h-4 w-4" />
              Add {isManager ? "client" : "friend"}
            </button>
          </div>
        }
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={isManager ? "Search clients…" : "Search friends…"}
              className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
            />
          </div>
        }
        summary={[
          { label: "Activity", value: sport === "all" ? "All" : sport,
            emoji: sport !== "all" ? sportEmoji[sport] : undefined,
            onClear: sport !== "all" ? () => setSport("all") : undefined },
        ]}
      >
        <OptionFilter
          label="Activity"
          value={sport as any}
          onChange={(v) => setSport(v as string)}
          options={sports.map((s) => ({ value: s, label: s, emoji: sportEmoji[s] }))}
        />
      </FilterCard>

      {/* Result count + org context */}
      <div className="flex items-center justify-between gap-3 px-1">
        <div className="text-xs text-slate-500">
          {filtered.length} in{" "}
          <span className="text-slate-700 font-medium inline-flex items-center gap-1">
            {org.id === GLOBAL_ORG_ID ? <Globe2 className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
            {org.name}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((a) => (
          <RosterCard key={a.id} a={a} isManager={isManager} />
        ))}
      </div>
    </div>
  );
}

function RosterCard({ a, isManager }: { a: typeof ATHLETES[number]; isManager: boolean }) {
  const isEmployee = a.kind === "employee";

  return (
    <div className="card p-4 hover:ring-1 hover:ring-el-200 transition group">
      <Link to={`/roster/${a.id}`} className="block">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-gradient-to-br from-el-200 to-el-400 text-white text-sm font-semibold flex items-center justify-center">
            {a.initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-slate-900 truncate group-hover:text-el-700">
                {a.name}
              </div>
              {isEmployee && a.risk ? (
                <RiskBadge risk={a.risk} />
              ) : (
                <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                  {a.activity}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 truncate flex items-center gap-1.5">
              <KindChip kind={a.kind} />
              {isEmployee
                ? <>{a.team ?? ""}{a.shift ? ` · ${a.shift} shift` : ""}</>
                : <>{a.team ?? "Independent"} · last seen {a.lastSeen}</>}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="label-eyebrow">
              {isEmployee ? "Assessments" : "Sessions"}
            </div>
            <div className="text-xl font-semibold text-slate-900">{a.sessions}</div>
          </div>
          <div>
            <div className="label-eyebrow text-right">
              {isEmployee ? "Risk trend" : "Quality trend"}
            </div>
            <Sparkline values={a.trend} />
          </div>
        </div>
      </Link>

      <div className="mt-4 border-t border-slate-100 pt-3 flex items-center gap-2">
        {isEmployee ? (
          <>
            <button className="btn-secondary !h-8 !text-xs">Open profile</button>
            <button className="btn-ghost !h-8 !text-xs">Assign training</button>
            <button className="btn-ghost !h-8 !text-xs">Add note</button>
          </>
        ) : a.kind === "client" ? (
          <>
            <button className="btn-secondary !h-8 !text-xs">Open profile</button>
            <button className="btn-ghost !h-8 !text-xs">Share report</button>
            <button className="btn-ghost !h-8 !text-xs">Add note</button>
          </>
        ) : a.kind === "athlete" && isManager ? (
          <>
            <button className="btn-secondary !h-8 !text-xs">Open profile</button>
            <button className="btn-ghost !h-8 !text-xs">Share report</button>
          </>
        ) : (
          <>
            <button className="btn-secondary !h-8 !text-xs">View latest</button>
            <button className="btn-ghost !h-8 !text-xs">
              <MessageCircle className="h-3.5 w-3.5" /> Cheer
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function KindChip({ kind }: { kind: AthleteKind }) {
  if (kind === "self") return null;
  const cls =
    kind === "employee" ? "bg-amber-50 text-amber-700 ring-amber-200" :
    kind === "client"   ? "bg-violet-50 text-violet-700 ring-violet-200" :
    kind === "friend"   ? "bg-slate-100 text-slate-600 ring-slate-200" :
                          "bg-el-50 text-el-700 ring-el-200";
  return (
    <span className={`badge ring-1 ${cls}`}>{KIND_LABEL[kind]}</span>
  );
}
